/**
 * The owner's view of a published app: who is using it, what they have sent, and how to
 * reach them.
 *
 * Thin wrappers over the API, web-owned. The shared versions do the same work but fetch
 * their token from CognitoService, which since SEC-14 stores the refresh token in
 * expo-secure-store and therefore pulls React Native into anything that imports it. These
 * take the token from the one request() instead, which is where it already lives.
 *
 * Pure helpers — sortNewestFirst, submissionsToCsv, lastSeen — are still imported from the
 * shared modules where they are pure; only the fetching moved.
 */
import { ApiError, request } from './client';

export interface SubmissionRow {
  [field: string]: unknown;
}

export interface SubmissionPage {
  rows: SubmissionRow[];
  /** Pass back to read the next page; absent when there are no more. */
  cursor?: string;
}

const PAGE = 200;

/** Newest first. Applied to everything accumulated, not one page at a time — rows are keyed
 *  by a random id, so the server returns them in no useful order and per-page sorting would
 *  interleave old replies among new ones on every "load more". */
export function sortNewestFirst(rows: SubmissionRow[]): SubmissionRow[] {
  return [...rows].sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
}

export async function fetchSubmissions(
  appId: string | undefined,
  collection: string,
  cursor?: string,
): Promise<SubmissionPage> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const query = `?limit=${PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const body = await request<{ items?: SubmissionRow[]; cursor?: string }>(
    `/apps/${encodeURIComponent(appId)}/${encodeURIComponent(collection)}${query}`,
  );
  return { rows: sortNewestFirst(body.items ?? []), cursor: body.cursor };
}

/** A spreadsheet of what people sent. Quotes every field: a reply containing a comma or a
 *  newline would otherwise silently shift every column after it. */
export function submissionsToCsv(rows: SubmissionRow[]): string {
  if (rows.length === 0) return '';
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const cell = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map(cell).join(','), ...rows.map((r) => columns.map((c) => cell(r[c])).join(','))].join('\n');
}

export interface AppUser {
  userId: string;
  email?: string;
  role: string;
  joinedAt?: string;
  lastActiveAt?: string;
}

export async function fetchAppUsers(appId: string | undefined): Promise<AppUser[]> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const body = await request<{ items?: AppUser[] }>(`/apps/${encodeURIComponent(appId)}/users`);
  return body.items ?? [];
}

/** Remove a person's data from a published app. Their own request, or the owner's. */
export async function removeAppUser(appId: string, userId: string): Promise<void> {
  await request(`/apps/${encodeURIComponent(appId)}/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export interface BroadcastResult {
  /** Confirmed subscribers the send was attempted to. */
  recipients: number;
  /** How many the mail provider accepted. */
  sent: number;
}

export async function sendBroadcast(
  appId: string | undefined,
  message: { subject: string; body: string; senderAddress: string },
): Promise<BroadcastResult> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  return request<BroadcastResult>(`/apps/${encodeURIComponent(appId)}/broadcast`, {
    method: 'POST',
    body: message,
  });
}

export interface AddressChange {
  slug: string;
  siteUrl?: string;
  /** Earlier addresses that still open this app. */
  keptWorking: string[];
}

/**
 * Move a published app to an address its owner chose. The old address keeps working:
 * somebody has that link, and a rename they never hear about is no reason for it to stop
 * opening.
 */
export async function changeSiteAddress(appId: string | undefined, desired: string): Promise<AddressChange> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const body = await request<{ slug?: string; siteUrl?: string; keptWorking?: string[] }>(
    `/apps/${encodeURIComponent(appId)}/web/address`,
    { method: 'PUT', body: { slug: desired } },
  );
  if (!body.slug) throw new ApiError('server', 'The server did not confirm the new address.');
  return { slug: body.slug, siteUrl: body.siteUrl, keptWorking: body.keptWorking ?? [] };
}

/**
 * When someone was last here, in words.
 *
 * Copied rather than imported: the shared appUsers module is a fine home for it, but that
 * module fetches a token from CognitoService, so importing one function from it would drag
 * React Native into the bundle.
 */
export function lastSeen(user: AppUser, now: Date = new Date()): string {
  if (!user.lastActiveAt) return 'Not used yet';
  const then = new Date(user.lastActiveAt).getTime();
  if (Number.isNaN(then)) return 'Not used yet';
  const minutes = Math.floor((now.getTime() - then) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}
